import { useCallback, useMemo } from "react";
import { type UseFormReturn } from "react-hook-form";

import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";
import { useLoadingScreen } from "@/components/features/side-effect/show-loading-screen-to-user/_hooks/use-loading-screen";

import type { ButtonProps } from "@/components/ui/button/button";
import { Button } from "@/components/ui/button/button";

import { useContactUpdateMutation } from "../../api";

import type { ContactUpdateInputZodSchemaType } from "../../schema";

type Props = {
	form: UseFormReturn<ContactUpdateInputZodSchemaType>;
};

export const HeaderRightButton = ({ form }: Props) => {
	const { updateContact } = useContactUpdateMutation();
	const { showLoadingScreenCallback, hideLoadingScreenCallback } = useLoadingScreen();
	const { formState, reset, getValues } = form;
	const { isValid, isDirty } = formState;

	const buttonVariant = useMemo<ButtonProps["variant"]>(() => {
		if (!isValid || !isDirty) {
			return "disabled";
		}

		return "default";
	}, [isValid, isDirty]);

	const handleClick = useCallback(async () => {
		showLoadingScreenCallback();
		const contact = getValues();
		const { data, errors } = await updateContact(contact).finally(() => {
			hideLoadingScreenCallback();
		});

		if (!data || errors) {
			notifyUserEventInfo({
				variant: "error",
				title: "問い合わせ内容の送信に失敗しました",
				description:
					"お手数をおかけしますが、時間を置いてから再度お問い合わせいただきますようお願いいたします。",
			});
			return;
		}

		notifyUserEventInfo({
			title: "問い合わせ内容を送信しました",
			description: "担当者より返信いたします",
		});
		reset();
	}, [getValues, hideLoadingScreenCallback, reset, showLoadingScreenCallback, updateContact]);

	return (
		<Button size="xl" variant={buttonVariant} onClick={handleClick}>
			送信
		</Button>
	);
};

HeaderRightButton.displayName = "HeaderRightButton";
